import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToNotifications, markNotificationAsRead, deleteNotification } from '../services/firebaseService';
import { createTestNotification } from '../utils/testNotifications';
import { createTestMentionNotification } from '../utils/testMentions';
import { debugUserNotifications, createTestAssignmentNotification, createTestMentionNotification as debugCreateTestMentionNotification, debugUserData } from '../utils/debugNotifications';
import { testUserNotificationCreation, compareAdminUserNotifications } from '../utils/testUserNotifications';
import { checkAndFixCurrentUserRole, fixUsersWithoutRoles } from '../utils/fixUserRoles';
import { debugAssignmentNotification, debugMentionNotification, testWithCurrentUsers } from '../utils/debugRealNotifications';
import { testNotificationsByRole, testNotificationSubscriptionsByRole, debugFirestoreAccess } from '../utils/debugRoleBasedNotifications';

const NotificationsTab = ({ onNavigateToTask, onNavigateToActivity, onNavigateToProject }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      console.log('No current user, skipping notifications subscription');
      setLoading(false);
      return;
    }

    console.log('Setting up notifications subscription for user:', currentUser.uid);
    console.log('Current user object:', currentUser);
    
    const unsubscribe = subscribeToNotifications(currentUser.uid, (notificationsData) => {
      console.log('Received notifications data in component:', notificationsData);
      setNotifications(notificationsData);
      
      // Calculate unread count
      const unread = notificationsData.filter(notification => !notification.read).length;
      setUnreadCount(unread);
      
      setLoading(false);
    });

    return () => {
      console.log('Cleaning up notifications subscription');
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]);

  const handleNotificationClick = async (notification) => {
    console.log('Notification clicked:', notification);
    
    // Mark as read if unread
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type and available data
    if (notification.type === 'message_mention' && notification.taskId) {
      console.log('Navigating to task messaging:', {
        projectId: notification.projectId,
        activityId: notification.activityId,
        taskId: notification.taskId
      });
      // Navigate to task messaging
      if (onNavigateToTask) {
        onNavigateToTask(notification.projectId, notification.activityId, notification.taskId, true); // Pass true for chat
      }
    } else if (notification.type === 'task_assignment' && notification.taskId) {
      console.log('Navigating to task assignment:', {
        projectId: notification.projectId,
        activityId: notification.activityId,
        taskId: notification.taskId
      });
      // Navigate to specific task
      if (onNavigateToTask) {
        onNavigateToTask(notification.projectId, notification.activityId, notification.taskId);
      }
    } else if (notification.type === 'activity_assignment' && notification.activityId) {
      console.log('Navigating to activity assignment:', {
        projectId: notification.projectId,
        activityId: notification.activityId
      });
      // Navigate to specific activity
      if (onNavigateToActivity) {
        onNavigateToActivity(notification.projectId, notification.activityId);
      }
    } else if (notification.projectId) {
      console.log('Navigating to project fallback:', notification.projectId);
      // Navigate to project as fallback
      if (onNavigateToProject) {
        onNavigateToProject(notification.projectId);
      }
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleCreateTestNotification = async () => {
    if (!currentUser) return;
    try {
      await createTestNotification(currentUser.uid);
    } catch (error) {
      console.error('Error creating test notification:', error);
    }
  };

  const handleCreateTestMentionNotification = async () => {
    if (!currentUser) return;
    try {
      await createTestMentionNotification(currentUser.uid, currentUser.uid);
    } catch (error) {
      console.error('Error creating test mention notification:', error);
    }
  };

  const handleDebugNotifications = async () => {
    if (!currentUser) return;
    try {
      console.log('🔍 Starting notification debug...');
      await debugUserData(currentUser.uid);
      await debugUserNotifications(currentUser.uid);
    } catch (error) {
      console.error('Error debugging notifications:', error);
    }
  };

  const handleCreateTestAssignment = async () => {
    if (!currentUser) return;
    try {
      await createTestAssignmentNotification(currentUser.uid, currentUser.displayName || currentUser.email);
    } catch (error) {
      console.error('Error creating test assignment notification:', error);
    }
  };

  const handleCreateTestMention = async () => {
    if (!currentUser) return;
    try {
      await debugCreateTestMentionNotification(currentUser.uid, currentUser.displayName || currentUser.email);
    } catch (error) {
      console.error('Error creating test mention notification:', error);
    }
  };

  const handleTestUserNotification = async () => {
    if (!currentUser) return;
    try {
      await testUserNotificationCreation(currentUser.uid, currentUser.email);
    } catch (error) {
      console.error('Error testing user notification:', error);
    }
  };

  const handleCompareNotifications = async () => {
    if (!currentUser) return;
    try {
      // For comparison, we need admin and user IDs
      // This will show the difference in console
      console.log('🔄 Starting admin vs user notification comparison...');
      console.log('Current user ID:', currentUser.uid);
      console.log('Current user email:', currentUser.email);
      
      // Get all users to find admin and regular user for comparison
      const usersData = await debugUserData(currentUser.uid);
      const adminUser = usersData.find(u => u.role === 'admin' || u.role === 'super_admin');
      const regularUser = usersData.find(u => u.role === 'user' || !u.role);
      
      if (adminUser && regularUser) {
        await compareAdminUserNotifications(adminUser.id, regularUser.id);
      } else {
        console.log('⚠️ Could not find both admin and regular user for comparison');
      }
    } catch (error) {
      console.error('Error comparing notifications:', error);
    }
  };

  const handleFixUserRole = async () => {
    if (!currentUser) return;
    try {
      await checkAndFixCurrentUserRole(currentUser);
    } catch (error) {
      console.error('Error fixing user role:', error);
    }
  };

  const handleFixAllUserRoles = async () => {
    try {
      await fixUsersWithoutRoles();
    } catch (error) {
      console.error('Error fixing all user roles:', error);
    }
  };

  const handleDebugAssignment = async () => {
    if (!currentUser) return;
    try {
      // Test assignment with current user's info
      await debugAssignmentNotification(currentUser.displayName || currentUser.email, currentUser);
    } catch (error) {
      console.error('Error debugging assignment:', error);
    }
  };

  const handleDebugMention = async () => {
    if (!currentUser) return;
    try {
      // Test mention with current user's info
      const testMentionText = `Hey @${currentUser.displayName || currentUser.email}, can you check this?`;
      await debugMentionNotification(testMentionText, currentUser);
    } catch (error) {
      console.error('Error debugging mention:', error);
    }
  };

  const handleTestRealScenarios = async () => {
    try {
      await testWithCurrentUsers();
    } catch (error) {
      console.error('Error testing real scenarios:', error);
    }
  };

  const handleTestRoleBasedNotifications = async () => {
    try {
      await testNotificationsByRole();
    } catch (error) {
      console.error('Error testing role-based notifications:', error);
    }
  };

  const handleTestRoleSubscriptions = async () => {
    try {
      await testNotificationSubscriptionsByRole();
    } catch (error) {
      console.error('Error testing role subscriptions:', error);
    }
  };

  const handleDebugFirestoreAccess = async () => {
    if (!currentUser) return;
    try {
      await debugFirestoreAccess(currentUser);
    } catch (error) {
      console.error('Error debugging Firestore access:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading notifications...</div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-gray-500">No notifications yet</div>
        
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Notifications</h2>
            <p className="text-sm text-gray-600">Stay updated on your assignments and project activities</p>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {unreadCount} unread
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`px-6 py-4 hover:bg-gray-50 cursor-pointer ${
              !notification.read ? 'bg-blue-50 border-l-4 border-blue-500' : ''
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 h-2 w-2 rounded-full ${
                    !notification.read ? 'bg-blue-600' : 'bg-gray-300'
                  }`}></div>
                  <h4 className="ml-3 text-sm font-medium text-gray-900">
                    {notification.title}
                  </h4>
                </div>
                <p className="mt-1 ml-5 text-sm text-gray-600">
                  {notification.message}
                </p>
                
                {/* Project and Activity Info */}
                <div className="mt-2 ml-5 flex flex-wrap items-center gap-2 text-xs">
                  {notification.projectName && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                      <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {notification.projectName}
                    </span>
                  )}
                  {notification.activityName && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                      <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {notification.activityName}
                    </span>
                  )}
                  {notification.taskName && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-50 text-green-700">
                      <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {notification.taskName}
                    </span>
                  )}
                </div>

                <div className="mt-2 ml-5 flex items-center text-xs text-gray-500">
                  <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {formatDate(notification.createdAt)}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {notification.type === 'task_assignment' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Task
                  </span>
                )}
                {notification.type === 'activity_assignment' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Activity
                  </span>
                )}
                {notification.type === 'test' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Test
                  </span>
                )}
                {notification.type === 'message_mention' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Mention
                  </span>
                )}
                <div className="flex items-center space-x-1">
                  {!notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 rounded hover:bg-blue-50"
                    >
                      Mark as read
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteNotification(notification.id);
                    }}
                    className="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-50"
                    title="Delete notification"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationsTab;
